import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from '../services/toast.service';
import { SKIP_GLOBAL_ERROR_TOAST } from './http-context.tokens';

export interface NormalizedHttpError {
  message: string;
  status: number;
  success: 'Success' | 'Failed';
}

@Injectable()
export class ErrorInterceptor
  implements HttpInterceptor {

  constructor(
    private toastService: ToastService
  ) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    return next.handle(request).pipe(
      catchError(err => {

        let obj: NormalizedHttpError;
        const status = err?.status ?? 0;
        const resolvedMessage =
          this.extractMessage(err?.error) ||
          this.getFallbackMessage(status);

        obj = {
          message: resolvedMessage,
          status,
          success:
            err?.error?.success == true
              ? 'Success'
              : 'Failed'
        };

        if (!request.context.get(SKIP_GLOBAL_ERROR_TOAST)) {
          const statusTitle = this.getStatusTitle(obj.status);

          this.toastService.showToaster(
            `${statusTitle} (Error Code: ${obj.status})`,
            obj.message,
            'error'
          );
        }

        return throwError(() => obj);
      })
    );
  }

  private extractMessage(body: unknown): string | undefined {

    const response = body as any;

    if (typeof response.message === 'string' && response.message.trim()) {
      return response.message.trim();
    }

    return undefined;
  }

  private joinMessages(messages: unknown[]): string | undefined {
    const normalizedMessages = messages
      .map(message => String(message).trim())
      .filter(Boolean);

    return normalizedMessages.length
      ? normalizedMessages.join(', ')
      : undefined;
  }

  private getStatusTitle(status: number): string {
    if (status === 0) return 'Connection Error';
    if (status === 400) return 'Request Failed';
    if (status === 401) return 'Unauthorized';
    if (status === 403) return 'Forbidden';
    if (status === 404) return 'Not Found';
    if (status === 409) return 'Conflict';
    if (status === 422) return 'Validation Failed';
    if (status === 429) return 'Too Many Requests';
    if (status >= 500) return 'Server Error';
    return 'Failed';
  }

  private getFallbackMessage(status: number): string {
    if (status === 0) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    if (status === 400) {
      return 'Please check the submitted information.';
    }
    if (status === 401) {
      return 'Your session has expired. Please login again.';
    }
    if (status === 403) {
      return 'You do not have permission to perform this action.';
    }
    if (status === 404) {
      return 'Requested information was not found.';
    }
    if (status === 409) {
      return 'The requested information conflicts with an existing record.';
    }
    if (status === 422) {
      return 'Some submitted information is invalid.';
    }
    if (status === 429) {
      return 'Too many requests. Please try again later.';
    }
    if (status >= 500) {
      return 'Server error occurred. Please try again later.';
    }
    return 'Something went wrong. Please try again.';
  }
}
